import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {filterSpeckitCommands} from '../constants/speckitCommands';
import './SlashCommandMenu.css';

/**
 * Floating autocomplete menu that appears when the user types a `/speckit` prefix.
 *
 * Props:
 *  - prefix        {string}   Current text in the input (used to filter commands)
 *  - selectedIndex {number}   Currently highlighted row index
 *  - onSelect      {Function} Called with the full command string when an item is chosen
 *  - onClose       {Function} Called when the menu should be dismissed
 */
const SlashCommandMenu = ({prefix, selectedIndex, onSelect, onClose, commands: commandsProp}) => {
    const commands = commandsProp || filterSpeckitCommands(prefix);
    const listRef = useRef(null);

    // Keep the highlighted item scrolled into view
    useEffect(() => {
        if (listRef.current) {
            const active = listRef.current.querySelector('.slash-cmd-item--active');
            if (active) {
                active.scrollIntoView({block: 'nearest'});
            }
        }
    }, [selectedIndex]);

    if (!commands.length) return null;

    return (
        <div className="slash-cmd-menu" role="listbox" aria-label="Spec-Kit commands">
            <div className="slash-cmd-header">
                <span className="slash-cmd-header__logo">🌱</span>
                <span className="slash-cmd-header__title">Spec-Kit</span>
                <span className="slash-cmd-header__subtitle">Spec-Driven Development</span>
                <button
                    aria-label="Close command menu"
                    className="slash-cmd-close"
                    type="button"
                    onClick={onClose}
                >
                    ×
                </button>
            </div>

            <ul className="slash-cmd-list" ref={listRef} role="listbox">
                {commands.map((cmd, idx) => (
                    <li
                        key={cmd.command}
                        aria-selected={idx === selectedIndex}
                        className={`slash-cmd-item${idx === selectedIndex ? ' slash-cmd-item--active' : ''}`}
                        role="option"
                        onClick={() => onSelect(cmd.command)}
                        onMouseEnter={() => {/* highlight via CSS hover */}}
                    >
                        <span className="slash-cmd-item__icon">{cmd.icon}</span>
                        <span className="slash-cmd-item__body">
                            <span className="slash-cmd-item__command">{cmd.command}</span>
                            {cmd.argHint && (
                                <span className="slash-cmd-item__hint"> {cmd.argHint}</span>
                            )}
                            <span className="slash-cmd-item__desc">{cmd.description}</span>
                        </span>
                        {cmd.phase === 'core' && (
                            <span className="slash-cmd-item__badge slash-cmd-item__badge--core">core</span>
                        )}
                    </li>
                ))}
            </ul>

            <div className="slash-cmd-footer">
                <span>↑↓ navigate</span>
                <span>↵ select</span>
                <span>Esc close</span>
            </div>
        </div>
    );
};

SlashCommandMenu.propTypes = {
    prefix: PropTypes.string.isRequired,
    selectedIndex: PropTypes.number.isRequired,
    onSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    commands: PropTypes.array,
};

export default SlashCommandMenu;
